import './App.css';
import earcut from "earcut";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { BrowserRouter, Route, Routes } from 'react-router';
import Confi from './components/Confi/Confi';
import { Suspense, useEffect, useState } from 'react';
import { FirebaseApp, initializeApp } from "firebase/app";
import { getAuth, onAuthStateChanged, User, connectAuthEmulator } from "firebase/auth";
import Auth, { AuthConf } from './common/authentication/Auth';
import Login from './common/authentication//Login/Login';
import Action from './common/authentication/Action/Action';
import Home from './components/Home/Home';
import RequiredAuth from './common/authentication//RequiredAuth/RequiredAuth';
import { getFirestore, connectFirestoreEmulator } from '@firebase/firestore';
import { Alert, PaletteColor, Snackbar } from '@mui/material';
import { severity } from './common/models/severity.enum';
import { SnackbarService } from './common/services/snackbar.service';
import { ServiceManager } from './services/service-manager';
import { environment } from './environment.enum';
import ForgotPassword from './common/authentication//ForgotPassword/ForgotPassword';
import { connectStorageEmulator, getStorage } from 'firebase/storage';
import { reactEnvironmentKeys } from './react-environment-keys-enum';
import NewUser from './common/authentication//NewUser/NewUser';
import SignUp from './common/authentication//SignUp/SignUp';
import NotVerified from './common/authentication//NotVerified/NotVerified';
import { FirestoreService } from './common/firebase/firestore.service';
import RequiredAuthAdmin from './common/authentication//RequiredAuthAdmin/RequiredAuthAdmin';
import Admin from './components/Admin/Admin';
import NotAnAdmin from './common/authentication//NotAnAdmin/NotAnAdmin';
import DeleteAccount from './common/authentication//DeleteAccount/DeleteAccount';
import Explanations from './components/Common/Explanations/Explanations';
import { explanationIds, Ftue, UserData } from './common/authentication//models/user';
import { ExplanationService } from './common/services/explanation.service';
import { firestoreCollections } from './services/firestore-collections.enum';
import React from 'react';

import VR from './components/VR/VR.lazy';

(window as any).earcut = earcut;

const primaryColor: PaletteColor = {
    main: '#2819FF',
    light: '#2819FF',
    dark: '#2819FF',
    contrastText: '#ffffff',
}

const secondaryColor: PaletteColor = {
    main: '#ffffff',
    light: '#ffffff',
    dark: '#eeeeee',
    contrastText: '#000000',
}

const theme = createTheme({
    palette: {
        primary: primaryColor,
        secondary: secondaryColor
    },
    typography: {
        fontFamily: [
            'EamesCenturyModern',
        ].join(','),
    },
});

const App = () => {

    const [user, setUser] = useState<UserData>(undefined);
    const [initialised, setInitialised] = useState<boolean>(false);
    const [firstUpdate, setFirstUdpate] = useState<boolean>(false);
    const [firebaseApp, setFirebaseApp] = useState<FirebaseApp>(undefined)
    const [snackbar, setSnackbar] = useState<{ message: string, severity: severity, duration: number }>(undefined);
    const [shownExplanation, setShownExplanation] = useState<{ id: explanationIds }>(undefined);
    const [ftue, setFtue] = useState<Ftue>(undefined)
    const [authConf] = useState<AuthConf>({
        logoSrc: '/assets/mooslogo.svg',
        logoAlt: 'Logo of In the middle of our street BV',
        linkToCompany: 'https://wijzijnmoos.nl/',
        title: 'MOOS Maker App',
        subtitle: 'Configureer je eigen gebouw!',
        backgroundSrc: '/assets/auth-background.png',
    })
    const [authLinks] = useState({
        termsAndConditionsLink: 'https://wijzijnmoos.nl/wp-content/uploads/2022/08/Algemene-gebruiksvoorwaarden-ItMoos-juli-2022.pdf',
        privacyStatementLink: 'https://wijzijnmoos.nl/wp-content/uploads/2022/08/ItMoos-Privacy-statement-juli-2022.pdf',
    })

    const openSnackbar = (message: string, severityLevel: severity, duration = 6000) => {
        duration = severityLevel === severity.error ? undefined : duration;
        setSnackbar({ message, severity: severityLevel, duration });
    }

    const closeSnackbar = () => {
        setSnackbar(undefined);
    }

    const showExplanation = (id: explanationIds) => {
        setShownExplanation({ id });
    }

    const hideExplanation = () => {
        setShownExplanation(undefined);
    }

    const getFtue = async () => {
        const auth = getAuth();
        const s = await FirestoreService.getDoc(`${firestoreCollections.users}/${auth.currentUser.email}/data`, 'ftue', () => {
            setFtue({
                explanationsFinished: [],
                explanationsSkipAll: false,
            })
        });
        if (s) {
            setFtue(s);
        }
    }

    const getUserDataAndUpdate = async (user) => {

        getFtue();

        const pr = FirestoreService.getDoc(`users/${user.email}/data`, 'private', () => {
            setUser(user);
        });
        const pb = FirestoreService.getDoc(`users/${user.email}/data`, 'public', () => {
            setUser(user);
        });

        const res = await Promise.all([pr, pb]);

        const token = await user.getIdTokenResult();
        const u = { ...user, private: res[0], public: res[1], token };
        setUser(u);
    }

    useEffect(() => {
        if (initialised && !firebaseApp) {

            let environmentBasedFirebaseConfig = {
                apiKey: process.env[reactEnvironmentKeys.REACT_APP_FIREBASE_API_KEY],
                authDomain: process.env[reactEnvironmentKeys.REACT_APP_FIREBASE_AUTH_DOMAIN],
                projectId: process.env[reactEnvironmentKeys.REACT_APP_FIREBASE_PROJECT_ID],
                storageBucket: process.env[reactEnvironmentKeys.REACT_APP_FIREBASE_STORAGE_BUCKET],
                messagingSenderId: process.env[reactEnvironmentKeys.REACT_APP_FIREBASE_MESSAGING_SENDER_ID],
                appId: process.env[reactEnvironmentKeys.REACT_APP_FIREBASE_APP_ID],
                measurementId: process.env[reactEnvironmentKeys.REACT_APP_FIREBASE_MEASUREMENT_ID],
            };
            const fApp = initializeApp(environmentBasedFirebaseConfig);
            setFirebaseApp(fApp);
            if (process.env.REACT_APP_ENVIRONMENT === environment.local) {
                const db = getFirestore();
                connectFirestoreEmulator(db, 'localhost', 5943);

                const auth = getAuth();
                connectAuthEmulator(auth, "http://localhost:5944");

                const storage = getStorage();
                connectStorageEmulator(storage, "localhost", 9199);

                onAuthStateChanged(auth, async (user) => {
                    if (user) {
                        await getUserDataAndUpdate(user);
                    }
                    setFirstUdpate(true);
                });
            } else {
                getFirestore(fApp);
                const auth = getAuth();
                onAuthStateChanged(auth, async (user) => {
                    if (user) {
                        await getUserDataAndUpdate(user);
                    }
                    setFirstUdpate(true);
                });
            }

            const snackbarService = new SnackbarService(openSnackbar, closeSnackbar);
            const explanationService = new ExplanationService(showExplanation, hideExplanation);

            ServiceManager.snackbarService = snackbarService;
            ServiceManager.explanationService = explanationService;

        }
    }, [initialised, firebaseApp])

    useEffect(() => {
        if (!initialised) {
            setInitialised(true);
        }
    }, [initialised])

    return firebaseApp ? (
        <BrowserRouter>
            <div data-testid="App" className="App nonPrintable">
                <ThemeProvider theme={theme}>
                    {firstUpdate &&
                        <Routes>
                            <Route path="" element={<Home user={user} />}>
                                <Route path="auth" element={<Auth conf={authConf} />}>
                                    <Route path="login" element={<Login user={user} setUser={setUser} termsAndConditionsLink={authLinks.termsAndConditionsLink} privacyStatementLink={authLinks.privacyStatementLink} />} />
                                </Route>
                                <Route path="auth" element={<Auth conf={authConf} />}>
                                    <Route path="forgot-password" element={<ForgotPassword user={user} />} />
                                </Route>
                                <Route path="auth" element={<Auth conf={authConf} />}>
                                    <Route path="action" element={<Action user={user} setUser={setUser} />} />
                                </Route>
                                <Route path="auth" element={<Auth conf={authConf} />}>
                                    <Route path="forgot-password" element={<ForgotPassword user={user} />} />
                                </Route>
                                <Route path="auth" element={<Auth conf={authConf} />}>
                                    <Route path="new-user" element={<NewUser termsAndConditionsLink={authLinks.termsAndConditionsLink} privacyStatementLink={authLinks.privacyStatementLink} agreeEmailText="Ik wil nieuws en aanbiedingen van MOOS ontvangen." user={user} setUser={setUser} />} />
                                </Route>
                                <Route path="auth" element={<Auth conf={authConf} />}>
                                    <Route path="profile" element={<NewUser termsAndConditionsLink={authLinks.termsAndConditionsLink} privacyStatementLink={authLinks.privacyStatementLink} agreeEmailText="Ik wil nieuws en aanbiedingen van MOOS ontvangen." user={user} setUser={setUser} />} />
                                </Route>
                                <Route path="auth" element={<Auth conf={authConf} />}>
                                    <Route path="delete-account" element={<DeleteAccount user={user} />} />
                                </Route>
                                <Route path="auth" element={<Auth conf={authConf} />}>
                                    <Route path="not-verified" element={<NotVerified noreplyEmailAddress="noreply@itmoos.nl" user={user} setUser={setUser} />} />
                                </Route>
                                <Route path="auth" element={<Auth conf={authConf} />}>
                                    <Route path="not-an-admin" element={<NotAnAdmin />} />
                                </Route>
                                <Route path="auth" element={<Auth conf={authConf} />}>
                                    <Route path="signup" element={<SignUp termsAndConditionsLink={authLinks.termsAndConditionsLink} privacyStatementLink={authLinks.privacyStatementLink} />} />
                                </Route>
                                <Route element={<RequiredAuth user={user} />}>
                                    <Route path="app" element={<Confi user={user} />} />
                                    <Route path="vr" element={<VR />}>
                                    </Route>
                                    <Route path="vr" element={<VR />}>
                                    </Route>
                                </Route>
                                <Route element={<RequiredAuthAdmin user={user} />}>
                                    <Route path="admin" element={<Admin user={user} />} />
                                </Route>
                            </Route>
                        </Routes>
                    }
                    {ftue && <Explanations user={user} setUser={setUser} showedExplanation={shownExplanation} ftue={ftue} setFtue={setFtue} />}
                </ThemeProvider>
            </div>
            {snackbar && <Snackbar open={snackbar !== undefined} autoHideDuration={snackbar.duration} onClose={closeSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>}
        </BrowserRouter>
    ) : null;
}

export default App;
